import React from "react";
import {
  List,
  ListItemButton,
  Box,
  Typography,
  Tooltip,
  useTheme,
  alpha
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StorageIcon from '@mui/icons-material/Storage';
import { useSelector } from "react-redux";
import { DynamicIcon } from "../../utilities/iconUtils";



const EntitiesBar = ({
  advancedMode,
  entities,
  selectedEntity,
  setSelectedEntity,
  setFields,
  getSimpleFields,
  filterModalOpen,
  setFilterModalOpen,
  metadata,
}) => {

  const { selectedDatabase, selectedMetadata } = useSelector((store) => store.settings)
  const theme = useTheme();
  // Desired order for unstructured entities
  const desiredOrder = [
    "Chief Complaint History",
    "History of Present Illness",
    "Review of System",
    "Past Medical History",
    "Pregnancy Comments",
    "Physical Exam",
    "Assessment",
    "Plan",
    "Social History",
    "Family History",
  ];

  // Separate entities into standard and unstructured
  const standardEntities = entities.filter(entity => !entity.unstructured && !entity.disabled);
  const unstructuredEntities = entities.filter(entity => entity.unstructured);
  console.log({ standardEntities, unstructuredEntities })
  console.log('Entity icons:', standardEntities.map(e => ({ name: e.friendlyName, icon: e.icon })))
  // Sort unstructured entities based on the desired order
  const sortedUnstructuredEntities = unstructuredEntities.sort((a, b) => {
    const indexA = desiredOrder.findIndex(item => item.toLowerCase() === a.friendlyName.toLowerCase());
    const indexB = desiredOrder.findIndex(item => item.toLowerCase() === b.friendlyName.toLowerCase());

    // Handle cases where the friendlyName is not found in the desiredOrder
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });

  const handleEntitySelection = (friendlyName) => {
    setSelectedEntity((prevEntity) => {
      if (prevEntity?.friendlyName !== friendlyName) {
        const foundEntity = entities.find(
          (el) => el.friendlyName === friendlyName
        );
        if (foundEntity) {
          if (!advancedMode) {
            getSimpleFields(metadata, foundEntity, setFields);
          }
          setFilterModalOpen(!filterModalOpen);
          return foundEntity;
        }
      } else {
        setFilterModalOpen(!filterModalOpen);
        return prevEntity;
      }
    });
  };

  return (
    <Box
      sx={{
        height: "83vh",
        minWidth: "45%",
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.surface.subtle,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1
      }}
    >
      {/* <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
            color: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          {selectedDatabase}
        </Typography>
      </Box> */}

      <Box sx={{
        overflowY: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>

        {/* PATIENT DATA header */}
        <Typography
          variant="h6"
          sx={{
            padding: '16px',
            color: theme.palette.text.secondary,
            flexShrink: 0
          }}
        >
          PATIENT DATA
        </Typography>

        {/* Standard entities list */}
        <List sx={{ padding: 0, flexShrink: 0 }} disablePadding>
          {standardEntities.map((entity, index) => (
            <ListItemButton
              key={index}
              disabled={entity.disabled}
              onClick={() => handleEntitySelection(entity.friendlyName)}
              sx={{
                padding: '12px 16px',
                backgroundColor: selectedEntity?.friendlyName === entity.friendlyName
                  ? alpha(theme.palette.primary.main, 0.08)
                  : 'transparent',
                '&:hover': {
                  backgroundColor: selectedEntity?.friendlyName === entity.friendlyName
                    ? alpha(theme.palette.primary.main, 0.12)
                    : alpha(theme.palette.action.hover, 0.04),
                },
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <DynamicIcon
                  name={entity.icon || 'Circle'}
                  size={14}
                  style={{
                    color: selectedEntity?.friendlyName === entity.friendlyName
                      ? theme.palette.text.secondary
                      : theme.palette.text.secondary
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: selectedEntity?.friendlyName === entity.friendlyName ? 500 : 400,
                    color: 'inherit'
                  }}
                >
                  {entity.friendlyName}
                </Typography>
              </Box>

              {entity.enableTooltip && (
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        Definition:
                      </Typography>
                      <Typography sx={{ mb: 2 }}>
                        {entity.tooltip?.definition}
                      </Typography>
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        Example:
                      </Typography>
                      <Typography>
                        {entity.tooltip?.example}
                      </Typography>
                    </Box>
                  }
                >
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{
                      color: theme.palette.text.disabled,
                      fontSize: '18px',
                      opacity: 0.7
                    }}
                  />
                </Tooltip>
              )}
            </ListItemButton>
          ))}
        </List>

        {/* UNSTRUCTURED DATA header and list - only show if there are unstructured entities */}
        {sortedUnstructuredEntities.length > 0 && (
          <>
            <Typography
              variant="h5 "
              sx={{
                padding: '16px',
                paddingTop: '32px',
                color: theme.palette.text.secondary,
                flexShrink: 0
              }}
            >
              Clinical Notes
            </Typography>

            <List sx={{ padding: 0, flexShrink: 0 }} disablePadding>
              {sortedUnstructuredEntities.map((entity, index) => (
                <ListItemButton
                  key={`unstructured-${index}`}
                  disabled={entity.disabled}
                  onClick={() => handleEntitySelection(entity.friendlyName)}
                  sx={{
                    padding: '12px 16px',
                    backgroundColor: selectedEntity?.friendlyName === entity.friendlyName
                      ? alpha('#e8b94a', 0.12)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: selectedEntity?.friendlyName === entity.friendlyName
                        ? alpha('#e8b94a', 0.18)
                        : alpha(theme.palette.action.hover, 0.04),
                    },
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <DynamicIcon
                      name={entity.icon || 'Circle'}
                      size={14}
                      style={{
                        color: selectedEntity?.friendlyName === entity.friendlyName
                          ? theme.palette.common.white
                          : theme.palette.text.secondary
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: selectedEntity?.friendlyName === entity.friendlyName
                          ? (theme.palette.mode === 'dark' ? '#f0c674' : '#b8860b')
                          : theme.palette.text.primary,
                        fontWeight: selectedEntity?.friendlyName === entity.friendlyName ? 500 : 400
                      }}
                    >
                      {entity.friendlyName}
                    </Typography>
                  </Box>

                  {entity.enableTooltip && (
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography sx={{ fontWeight: 600, mb: 1 }}>
                            Definition:
                          </Typography>
                          <Typography sx={{ mb: 2 }}>
                            {entity.tooltip?.definition}
                          </Typography>
                          <Typography sx={{ fontWeight: 600, mb: 1 }}>
                            Example:
                          </Typography>
                          <Typography>
                            {entity.tooltip?.example}
                          </Typography>
                        </Box>
                      }
                    >
                      <InfoOutlinedIcon
                        fontSize="small"
                        sx={{
                          color: theme.palette.text.disabled,
                          fontSize: '18px',
                          opacity: 0.7
                        }}
                      />
                    </Tooltip>
                  )}
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </Box>
    </Box>
  );
};

export default EntitiesBar;